import { useState } from "react";
import { Wallet as WalletIcon, Snowflake, CreditCard, Flame, Plus, Pencil, Trash2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { StatusBadge, fmtUSD } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Loading from "@/components/dashboard/Loading";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

const EMPTY = { agent: "", balance: 0, currency: "USD", card: "•• 0000", status: "Active" };

export default function Wallets() {
  const { data, isLoading } = useDashboard();
  const { setWalletStatus, createWallet, updateWallet, deleteWallet } = useDashboardActions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  if (isLoading || !data) {
    return (<div><PageHeader title="Wallets" subtitle="Virtual treasury and agent-level card wallets." /><Loading /></div>);
  }

  const wallets = data.wallets;
  const total = wallets.reduce((s, w) => s + w.balance, 0);

  const toggleFreeze = async (w) => {
    const next = w.status === "Active" ? "Frozen" : "Active";
    await setWalletStatus(w.id, next);
    toast.success(`${w.agent}'s wallet ${next === "Frozen" ? "frozen" : "unfrozen"}`);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ agent: w.agent, balance: w.balance, currency: w.currency, card: w.card, status: w.status });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.agent.trim()) { toast.error("Wallet owner is required"); return; }
    setSaving(true);
    const body = {
      agent: form.agent.trim(),
      balance: Number(form.balance) || 0,
      currency: form.currency || "USD",
      card: form.card || "•• 0000",
      status: form.status,
    };
    try {
      if (editing) { await updateWallet(editing.id, body); toast.success("Wallet updated"); }
      else { await createWallet(body); toast.success("Wallet created"); }
      setDialogOpen(false);
    } catch { toast.error("Could not save wallet"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await deleteWallet(toDelete.id); toast.success("Wallet deleted"); }
    catch { toast.error("Could not delete wallet"); }
    finally { setToDelete(null); }
  };

  return (
    <div>
      <PageHeader title="Wallets" subtitle="Virtual treasury and agent-level card wallets.">
        <Button onClick={openCreate} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="add-wallet-btn">
          <Plus className="h-4 w-4" /> New wallet
        </Button>
      </PageHeader>

      <Panel className="p-6 mb-5">
        <p className="text-xs text-slate-400">Total treasury balance</p>
        <p className="mt-1 text-3xl font-semibold text-white">{fmtUSD(total)}</p>
        <p className="mt-1 text-xs text-slate-500">{wallets.length} wallets · USD pool</p>
      </Panel>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {wallets.map((w) => {
          const frozen = w.status === "Frozen";
          return (
            <Panel key={w.id} className="p-5 vora-card-hover" data-testid={`wallet-card-${w.id}`}>
              <div className="flex items-center justify-between">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${frozen ? "bg-red-500/12 text-red-400" : "bg-[#4F8CFF]/12 text-[#4F8CFF]"}`}>
                  <WalletIcon className="h-5 w-5" />
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(w)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid={`wallet-edit-${w.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setToDelete(w)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`wallet-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  <StatusBadge status={w.status} />
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-400">{w.agent}</p>
              <p className="mt-1 text-2xl font-semibold text-white">{fmtUSD(w.balance)}</p>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <CreditCard className="h-3.5 w-3.5" /> {w.currency} · Card {w.card}
              </div>
              <Button size="sm" variant="outline" onClick={() => toggleFreeze(w)}
                className={`mt-4 w-full rounded-full gap-1.5 bg-transparent ${frozen ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" : "border-white/12 text-white hover:bg-white/5"}`}
                data-testid={`wallet-freeze-${w.id}`}>
                {frozen ? <><Flame className="h-3.5 w-3.5" /> Unfreeze</> : <><Snowflake className="h-3.5 w-3.5" /> Freeze</>}
              </Button>
            </Panel>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white" data-testid="wallet-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit wallet" : "New wallet"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Wallet owner / agent</Label>
              <Input value={form.agent} onChange={(e) => setForm({ ...form, agent: e.target.value })} placeholder="e.g. Sales Agent" className="bg-white/[0.03] border-white/12 text-white" data-testid="wallet-form-agent" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Balance ($)</Label>
                <Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="wallet-form-balance" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="wallet-form-currency" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Card label</Label>
                <Input value={form.card} onChange={(e) => setForm({ ...form, card: e.target.value })} className="bg-white/[0.03] border-white/12 text-white" data-testid="wallet-form-card" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="wallet-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {["Active", "Frozen"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="wallet-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Create wallet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0A1119] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete wallet?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This permanently removes {toDelete?.agent}'s wallet and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="wallet-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
